/* PhotoLightbox — full-screen photo viewer used across all curated
 * public media surfaces (Events, Community Updates, Distributions,
 * FeaturedGallery). Unified in Phase D.
 *
 * Behaviour:
 *  - Portal-free fixed overlay at z-[100000] so it sits above the
 *    floating Donate + Language tabs (z-[99999]) on mobile — otherwise
 *    those tabs would overlay tall/portrait photos.
 *  - `object-contain` so portrait / landscape / square are all shown
 *    at natural aspect; never crops.
 *  - Height caps at `100dvh` on mobile so very tall images don't push
 *    the controls off screen.
 *  - Prev / next / counter hidden when photos.length <= 1.
 *  - Keyboard: ← → for nav, ESC to close.
 *  - Touch swipe: horizontal drag > 50px triggers next/prev.
 *  - Backdrop click / X button both close.
 *  - Body scroll-locked while open (restores previous overflow).
 *  - Announces "Photo N of M" via aria-live so screen readers track nav.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const PhotoLightbox = ({ photos, open, startIndex = 0, title = '', onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const total = Array.isArray(photos) ? photos.length : 0;
  const single = total <= 1;

  // Reset the visible index whenever the caller opens the lightbox at
  // a different starting photo (e.g. clicked featured vs an additional
  // thumbnail).
  useEffect(() => {
    if (open) setIdx(Math.max(0, Math.min(startIndex, total - 1)));
  }, [open, startIndex, total]);

  const prev = useCallback(() => {
    if (single) return;
    setIdx((i) => (i - 1 + total) % total);
  }, [single, total]);

  const next = useCallback(() => {
    if (single) return;
    setIdx((i) => (i + 1) % total);
  }, [single, total]);

  // ESC + arrow-key handlers + body scroll lock. All wired only while
  // the lightbox is open so we don't fight the underlying detail
  // modal's own listeners.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key === 'ArrowLeft') { e.stopPropagation(); prev(); return; }
      if (e.key === 'ArrowRight') { e.stopPropagation(); next(); }
    };
    document.addEventListener('keydown', onKey, true); // capture so we win
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Phase D polish: signal to floating tabs (`[data-floating-tab]`)
    // that they should hide themselves while the lightbox is open. The
    // CSS rule in `index.css` fades them out. Additive class — never
    // touches any inline style on the tabs themselves.
    document.body.classList.add('lightbox-open');
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('lightbox-open');
    };
  }, [open, prev, next, onClose]);

  // Swipe: record start, threshold on end. Vertical scroll wins if
  // the user is scrolling the page down more than sideways.
  const onTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) { touchStartX.current = null; return; }
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current || 0);
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) next(); else prev();
  };

  if (!open || total === 0) return null;

  const currentUrl = photos[idx];
  const altBase = title ? title : 'Distribution photo';
  const altText = single ? altBase : `${altBase} — photo ${idx + 1} of ${total}`;

  return (
    <div
      className="fixed inset-0 z-[100000] bg-black/95 flex items-center justify-center touch-pan-y"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label={single ? 'Photo viewer' : `Photo viewer — showing ${idx + 1} of ${total}`}
      data-testid="photo-lightbox"
    >
      {/* Close */}
      <button type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="fixed top-3 right-3 sm:top-4 sm:right-4 z-10 bg-black/60 text-white p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        aria-label="Close photo viewer"
        data-testid="photo-lightbox-close">
        <X className="w-6 h-6" />
      </button>

      {/* Counter — hidden for single-photo distributions */}
      {!single && (
        <div
          className="fixed top-3 left-1/2 -translate-x-1/2 sm:top-4 z-10 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
          aria-live="polite"
          data-testid="photo-lightbox-counter">
          {idx + 1} of {total}
        </div>
      )}

      {/* Prev / Next — hidden when only one photo. Positioned so they
          never overlap the image on mobile. */}
      {!single && (
        <>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="fixed left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Previous photo"
            data-testid="photo-lightbox-prev">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="fixed right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Next photo"
            data-testid="photo-lightbox-next">
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Image — object-contain + max-h clamp keeps the whole photo
          on-screen regardless of aspect ratio. `100dvh` uses the true
          mobile viewport so iOS toolbars don't clip tall images. */}
      <img
        key={currentUrl}
        src={currentUrl}
        alt={altText}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[85vh] sm:max-h-[90vh] object-contain select-none"
        style={{ maxHeight: '85dvh' }}
        data-testid="photo-lightbox-image"
        draggable={false}
      />
    </div>
  );
};

export default PhotoLightbox;
